<template>
    <div :id="rootElementId"></div>
</template>

<script>
import * as d3v4 from "d3v4";
import * as _ from 'lodash';
import Dictionary from "../../dictionary";


export default {
  name: 'PassMatrix',
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false,
    },
    margin: {
      type: Object,
      required: false,
    },
    passes: {
      type: Array,
      required: false,
    },
    homeTeamScore: {
      type: Number,
      required: false
    },
    rivalTeamScore: {
      type: Number,
      required: false
    },
    teamName: {
      type: String,
      required: false
    },
    rivalTeamName: {
      type: String,
      required: false
    },
    minFrom: {
      type: Number,
      required: false
    },
    minTo: {
      type: Number,
      required: false
    },
    themeDark: {
      type: Boolean,
      required: false
    },
    language: {
      type: String,
      required: false
    },
  },
  computed: {
    rootElementId() {
      return `pitchPassMatrix${this.id}`;
    }
  },
  data() {
    return {
      height: 0,
      width: 0,
    }
  },
  mounted() {
    const self = this;

    const SVG_WIDTH = this.size ? this.size : 380;
    const CHART_SIZE_RATIO = SVG_WIDTH / 2 * 0.0022; //CHART_SIZE

    /* left: Y-axis + “From player”; bottom “To player” uses text-anchor end at negative x (needs ~228+ for long i18n + PNG export) */
    const margin = Object.assign(
      { top: 75, right: 72, bottom: 132, left: 228 },
      this.margin || {}
    );
    const width = SVG_WIDTH - margin.left * CHART_SIZE_RATIO - margin.right * CHART_SIZE_RATIO;
    const height = width * 0.67125;

    d3v4.selectAll('.nwTooltip').remove();

    // Total SVG size: inner plot + scaled margins (right must match width formula or labels clip on the right)
    const svgOuterWidth = width + margin.left * CHART_SIZE_RATIO + margin.right * CHART_SIZE_RATIO;
    const svgOuterHeight = height + margin.top * CHART_SIZE_RATIO + margin.bottom * CHART_SIZE_RATIO;

    var svg = d3v4
      .select(`#${this.rootElementId}`)
      .append("svg")
      .attr("width", svgOuterWidth)
      .attr("height", svgOuterHeight)
      .attr("overflow", "visible")
      .attr("id",`${this.rootElementId}SVG`)
      .append("g")
      .attr("transform", "translate(" + margin.left * CHART_SIZE_RATIO + "," + margin.top * CHART_SIZE_RATIO + ")");

    // Labels of row and columns -> unique identifier of the column called 'playerFromName' and 'playerToName'
    var data = this.passes.reduce(function(acc, d) {
      const found = acc.find(o => o.playerFromName === d.playerToName && o.playerToName === d.playerFromName);

      const newValue = { playerFromName: d.playerToName, playerToName: d.playerFromName, value: 1}
      if (!found && newValue.playerFromName !== newValue.playerToName)
        acc.push(newValue)
      else if (found)
        found.value++;
      return acc;
    }, []);

    var players = [...new Set(data.map(p => p.playerToName))].concat([...new Set(data.map(p => p.playerFromName))])

    players.forEach(function(p1) {
      players.forEach(function(p2) {
        const found = data.find(o => o.playerFromName === p1 && o.playerToName === p2);
        if (!found && p1 !== p2) {
          const emptyRow = { playerFromName: p1, playerToName: p2, value: 0}
          data.push(emptyRow);
        }
      });
    });

    // Build X scales and axis:
    var x = d3v4.scaleBand()
      .range([ 0, width ])
      .domain(players)
      .padding(0.05);

    svg.append("g")
      .style("font-size", 14 * CHART_SIZE_RATIO)
      .style("font-family", "NeueMontreal")
      .style("stroke-width", "1px")
      .style("fill", this.themeDark ? "#fff" : "#000")
      .attr("transform", "translate(0," + height + ")")
      .call(d3v4.axisBottom(x).tickSize(0))
      .selectAll("text")
      .style("text-anchor", "end")
      .style("fill", this.themeDark ? "#fff" : "#000")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)")
      .attr('id', function(d) { return "playerTo-" + d.replace(" ", "") })
      .select(".domain")
      .remove()

    // Build Y scales and axis:
    var y = d3v4.scaleBand()
      .range([ height, 0 ])
      .domain(players)
      .padding(0.05);

    svg.append("g")
      .style("font-size", 14 * CHART_SIZE_RATIO)
      .style("font-family", "NeueMontreal")
      .style("fill", this.themeDark ? "#fff" : "#000")
      .call(d3v4.axisLeft(y).tickSize(0))
      .selectAll("text")
      .style("fill", this.themeDark ? "#fff" : "#000")
      .attr('id', function(d) { return "playerFrom-" + d.replace(" ", "") })
      .select(".domain").remove()

    // Build color scale
    var myColor = d3v4.scaleLinear()
    .domain([1, 20])
    .range(['#3366FF', ''])
    .interpolate(d3.interpolateHcl);

    // create a nwTooltip
    var nwTooltip = d3v4.select("body")
      .append("div")
      .style("opacity", 0)
      .attr("class", "nwTooltip")
      .style("background-color", "white")
      .style("border", "solid")
      .style("border-width", 2 * CHART_SIZE_RATIO + "px")
      .style("border-radius", 5 * CHART_SIZE_RATIO + "px")
      .style("padding", 5 * CHART_SIZE_RATIO + "px")

    // Three function that change the nwTooltip when user hover / move / leave a cell
    var mouseover = function(d) {
      nwTooltip
        .style("opacity", 1)
      d3v4.select(this)
        .style("stroke", "black")
        .style("opacity", 1)

      let squareId = document.getElementById("square-" + d.playerFromName.replace(" ", "") + "-" + d.playerToName.replace(" ", "") + "-pitchPassingMatrix");

      const passWord =
        d.value === 1
          ? self.$t("game.passMap.passSingular")
          : self.$t("game.passMap.passPlural");
      const toWord = self.$t("game.gamePossessionMap.to");
      nwTooltip
        .style("font-size", 14 * CHART_SIZE_RATIO + "px")
        .html(
          "<div style=\"text-align:center;line-height:1.35;\"><b>" +
            d.value +
            " " +
            passWord +
            "</b><br/>" +
            d.playerToName +
            " " +
            toWord +
            " " +
            d.playerFromName +
            "</div>"
        )
        .style("width", "auto")
        .style("max-width", Math.min(420, window.innerWidth - 16) + "px")
        .style("height", "auto")
        .style("text-align", "center")
        .style("position", "fixed")
        .style("z-index", "10050")
        .style("box-sizing", "border-box")
        .style("display", "inline-block")
        .style("word-wrap", "break-word");

      const cellRect = squareId.getBoundingClientRect();
      const tipEl = nwTooltip.node();
      const tipW = tipEl.offsetWidth;
      const tipH = tipEl.offsetHeight;
      const pad = 8;
      const svgEl = document.getElementById(self.rootElementId + "SVG");
      const svgRect = svgEl ? svgEl.getBoundingClientRect() : null;

      let left = cellRect.left + cellRect.width / 2 - tipW / 2;
      let top = cellRect.top - tipH - pad;

      if (svgRect) {
        left = Math.max(svgRect.left + pad, Math.min(left, svgRect.right - tipW - pad));
        if (top < svgRect.top + pad) {
          top = cellRect.bottom + pad;
        }
        if (top + tipH > svgRect.bottom - pad) {
          top = Math.max(
            svgRect.top + pad,
            Math.min(cellRect.top + cellRect.height / 2 - tipH / 2, svgRect.bottom - tipH - pad)
          );
        }
      } else {
        left = Math.max(pad, Math.min(left, window.innerWidth - tipW - pad));
        if (top < pad) top = cellRect.bottom + pad;
        if (top + tipH > window.innerHeight - pad) {
          top = Math.max(pad, window.innerHeight - tipH - pad);
        }
      }

      nwTooltip
        .style("left", left + "px")
        .style("top", top + "px");


      d3v4.select("#playerFrom-" + d.playerToName.replace(" ", ""))
        .style("font-weight", 700)
        .style("font-size", 16 * CHART_SIZE_RATIO + "px")

      d3v4.select("#playerTo-" + d.playerFromName.replace(" ", ""))
        .style("font-weight", 700)
        .style("font-size", 16 * CHART_SIZE_RATIO + "px")
    }
    var mouseleave = function(d) {
      nwTooltip
        .style("opacity", 0)
      d3v4.select(this)
        .style("stroke", "none")
        .style("opacity", 0.8)

      d3v4.select("#playerFrom-" + d.playerToName.replace(" ", ""))
        .style("font-weight", 400)
        .style("font-size", 14 * CHART_SIZE_RATIO + "px")
      d3v4.select("#playerTo-" + d.playerFromName.replace(" ", ""))
        .style("font-weight", 400)
        .style("font-size",  14 * CHART_SIZE_RATIO + "px")
    }

    // add the squares
    var ssquares = svg.selectAll()
      .data(data, function(d) {return d.playerFromName+':'+d.playerToName;})
      .enter()
      .append("g");

      ssquares
      .append("rect")
        .attr("x", function(d) { return x(d.playerFromName) })
        .attr("y", function(d) { return y(d.playerToName) })
        .attr("rx", 4 * CHART_SIZE_RATIO)
        .attr("ry", 4 * CHART_SIZE_RATIO)
        .attr("width", x.bandwidth() )
        .attr("height", y.bandwidth() )
        .attr('id', function(d) { return "square-" + d.playerFromName.replace(" ", "") + "-" + d.playerToName.replace(" ", "") + "-pitchPassingMatrix"; })
        .style("fill", function(d) { return d.value === 0 ? "#bcbcbc" : myColor(d.value)} )
        .style("stroke-width", 4)
        .style("stroke", "none")
        .style("opacity", 0.8)
      .on("mouseover", mouseover)
      .on("mouseleave", mouseleave)

      ssquares
      .append("text")
      .attr("x", function(d) { return x(d.playerFromName) + x.bandwidth() / 2; })
      .attr("y", function(d) { return y(d.playerToName) + y.bandwidth() / 2; })
      .attr("dominant-baseline", "central")
      .attr("color", function(d) { return d.value > 0 ? "white" : "black"; })
      .style("font-family", "NeueMontreal")
      .style("font-size", 14 * CHART_SIZE_RATIO + "px")
      .attr("font-weight", 500)
      .attr("text-anchor", "middle")
      .style("pointer-events","none")
      .text( function(d) { return d.value; })

    svg.append("text")
      .attr("x", 0)
      .attr("y", -35 * CHART_SIZE_RATIO)
      .attr("text-anchor", "start")
      .attr("dy", "0em")
      .attr("font-weight", 500)
      .style("fill", this.themeDark ? "#fff" : "#000")
      .style("font-size", 24 * CHART_SIZE_RATIO + "px")
      .style("font-family", "NeueMontreal")
      .html(Dictionary[this.language]["game"]["passMap"]["passingMatrix"]);

    svg.append("text")
      .attr("x", 0)
      .attr("y", -35 * CHART_SIZE_RATIO)
      .attr("text-anchor", "start")
      .attr("dy", "1.4em")
      .attr("font-weight", 500)
      .style("fill", this.themeDark ? "#fff" : "#000")
      .style("font-size", 16 * CHART_SIZE_RATIO + "px")
      .style("font-family", "NeueMontreal")
      .html(this.teamName + " " + this.homeTeamScore + " - " + this.rivalTeamScore + " " + this.rivalTeamName);

    svg.append("text")
      .attr("x", -20 * CHART_SIZE_RATIO)
      .attr("y", -10 * CHART_SIZE_RATIO)
      .attr("text-anchor", "end")
      .attr("dy", "0em")
      .attr("font-weight", 700)
      .style("fill", this.themeDark ? "#fff" : "#000")
      .style("font-size", 16 * CHART_SIZE_RATIO + "px")
      .style("font-family", "NeueMontreal")
      .html(Dictionary[this.language]["table"]["connections"]["playerFrom"] + " &#8595;");

    /* Sit just under −45° column ticks (same band as names); old 192*ratio floor stranded the caption at the canvas bottom */
    const toPlayerCaptionY =
      height +
      Math.max(
        x.bandwidth() * 0.92 + 20 * CHART_SIZE_RATIO,
        34 * CHART_SIZE_RATIO
      );
    /* Bottom-left axis caption (right edge near plot origin; text extends left into margin) */
    svg.append("text")
      .attr("x", -88 * CHART_SIZE_RATIO)
      .attr("y", toPlayerCaptionY)
      .attr("text-anchor", "end")
      .attr("font-weight", 700)
      .style("fill", this.themeDark ? "#fff" : "#000")
      .style("font-size", 16 * CHART_SIZE_RATIO + "px")
      .style("font-family", "NeueMontreal")
      .html(Dictionary[this.language]["table"]["connections"]["playerTo"] + " &#8594;");

    /* Top-right logo: anchor to right edge of plot + right margin (inner coords) */
    const logoW = width * 0.19;
    const logoH = height * 0.19;
    const logoX = width + margin.right * CHART_SIZE_RATIO - logoW;
    this.drawImage(svg,
      logoX,
      - margin.top * CHART_SIZE_RATIO,
      logoW,
      logoH,
      require( this.themeDark ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png")
    );

    //Emit when is loaded
    this.$emit("loaded", this.rootElementId);

  },
  methods: {
    drawImage: function(svg, x, y, width, height, img) {
      svg.append("svg:image")
        .attr("id", "driblab-logo")
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img);
    },
  },
  destroyed(){
    d3v4.selectAll('.nwTooltip').remove();
  }
}

</script>



// WEBPACK FOOTER //
// src/views/base/PassMatrix.vue